import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { deliveryReportApi, subscriberApi } from '../../services/subscriberApi';
import { magazineApi as magApi } from '../../services/magazineApi';
import { dispatchApi } from '../../services/dispatchApi';
import { EmptyState, LoadingState } from '../Common/PanelState';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';
const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

function StatCard({ label, value, hint, icon, iconBg }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon path={icon} className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
          <div className="text-2xl font-extrabold text-gray-900 leading-tight">{value}</div>
          {hint ? <div className="text-xs text-gray-400 mt-0.5">{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

function runStatusBadge(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'shipped' || s === 'complete' || s === 'completed' || s === 'done') return 'bg-emerald-50 text-emerald-800 border-emerald-100';
  if (s === 'failed' || s === 'error') return 'bg-rose-50 text-rose-800 border-rose-100';
  if (s === 'pending' || s === 'queued') return 'bg-amber-50 text-amber-800 border-amber-100';
  return 'bg-slate-50 text-slate-800 border-slate-200';
}

export default function DeliveryReportsSection() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [issues, setIssues] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [reports, setReports] = useState([]);
  const [dispatchRuns, setDispatchRuns] = useState([]);
  const [filters, setFilters] = useState({ issue_id: '', city: '', search: '' });
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [r, s, m, runs] = await Promise.all([
        deliveryReportApi.getAll({ per_page: 50 }).catch(() => ({ data: { data: [] } })),
        subscriberApi.getAll({ per_page: 200 }).catch(() => ({ data: { data: [] } })),
        magApi.getPublishedIssues({ per_page: 100 }).catch(() => ({ data: { data_formatted: [] } })),
        dispatchApi.runs({ per_page: 20 }).catch(() => ({ data: { data: [] } })),
      ]);
      setReports(r?.data?.data || []);
      setSubscribers((s?.data?.data || []).filter((sub) => sub.status !== 'Inactive'));
      setIssues(m?.data?.data_formatted || m?.data?.data || []);
      setDispatchRuns(runs?.data?.data || []);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Could not load delivery reports';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectedIssue = issues.find((i) => String(i.id) === String(filters.issue_id));
  const filteredSubs = useMemo(() => subscribers.filter((s) => {
    if (filters.city && String(s.city || '').toLowerCase().indexOf(String(filters.city).toLowerCase()) === -1) return false;
    if (filters.search) {
      const q = String(filters.search).toLowerCase();
      if (!(String(s.name || '').toLowerCase().includes(q) || String(s.phone || '').includes(q))) return false;
    }
    return true;
  }), [subscribers, filters.city, filters.search]);

  const generateAll = async () => {
    if (!selectedIssue) { toast.warn('Select an issue first'); return; }
    setGenerating(true);
    try {
      await deliveryReportApi.exportExcel({ issue_id: selectedIssue.id, city: filters.city });
      toast.success('Export started');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Export failed');
    } finally {
      setGenerating(false);
    }
  };

  const filtersActive = Boolean(filters.issue_id || filters.city || filters.search);
  const clearFilters = () => setFilters({ issue_id: '', city: '', search: '' });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="admin-page-title">Delivery Reports</h1>
          <p className="admin-page-sub">
            Packing Desk runs and legacy exports.{' '}
            <Link to="/magazine/packing" className="font-semibold underline" style={{ color: FLARE_RED }}>Open Packing Desk</Link>
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm self-start sm:self-auto"
        >
          <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Dispatch runs"
          value={dispatchRuns.length}
          hint="Recent packing"
          icon="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          iconBg={FLARE_RED}
        />
        <StatCard
          label="Active subscribers"
          value={subscribers.length}
          hint="Eligible list"
          icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          iconBg="#4f46e5"
        />
        <StatCard
          label="Filter match"
          value={filteredSubs.length}
          hint="Current filters"
          icon="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          iconBg="#059669"
        />
        <StatCard
          label="Legacy reports"
          value={reports.length}
          hint="Older exports"
          icon="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          iconBg="#d97706"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Issue</label>
            <select
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white"
              value={filters.issue_id}
              onChange={(e) => setFilters((f) => ({ ...f, issue_id: e.target.value }))}
            >
              <option value="">All issues</option>
              {issues.map((i) => <option key={i.id} value={i.id}>{i.title || i.month_year}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">City</label>
            <input
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
              placeholder="Lahore"
              value={filters.city}
              onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Search subscribers</label>
            <input
              type="search"
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
              placeholder="Name or phone…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              disabled={generating}
              onClick={generateAll}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-bold shadow text-sm disabled:opacity-50"
              style={{ backgroundColor: FLARE_RED }}
            >
              <Icon path="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" className="h-4 w-4" />
              {generating ? 'Exporting…' : 'Export filtered'}
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: '#fff1f2', color: FLARE_RED_DARK, border: '1px solid rgba(196,21,28,0.15)' }}
          >
            <Icon path="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" className="h-3.5 w-3.5" />
            {filteredSubs.length} subscribers in filter
          </div>
          {filtersActive ? (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-1 px-2.5 py-1 rounded-md bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900">Recent Packing Desk runs</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Updating…' : `${dispatchRuns.length} ${dispatchRuns.length === 1 ? 'run' : 'runs'}`}
            </div>
          </div>
        </div>
        {loading ? (
          <LoadingState label="Loading delivery reports" />
        ) : loadError && dispatchRuns.length === 0 && reports.length === 0 ? (
          <EmptyState title="Could not load delivery reports" hint={loadError}>
            <button type="button" onClick={load} className="px-6 py-2.5 rounded-lg text-white font-bold shadow" style={{ backgroundColor: FLARE_RED }}>Try again</button>
          </EmptyState>
        ) : dispatchRuns.length === 0 ? (
          <EmptyState title="No dispatch runs yet" hint="Packing Desk runs will appear here after you generate or ship a queue." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">ID</th>
                  <th className="px-6 py-3 text-left font-semibold">Type</th>
                  <th className="px-6 py-3 text-left font-semibold">Issue</th>
                  <th className="px-6 py-3 text-left font-semibold">Counts</th>
                  <th className="px-6 py-3 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dispatchRuns.map((run, idx) => (
                  <tr key={run.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                    <td className="px-6 py-4 font-bold text-gray-900">#{run.id}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-indigo-50 text-indigo-800 border-indigo-100">
                        {run.type || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700">{run.magazine_issue?.title || run.magazine_issue_id || '—'}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {run.total_count ?? 0} total · {run.free_count ?? 0} free · {run.order_count ?? 0} orders · {run.shipped_count ?? 0} shipped
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${runStatusBadge(run.status)}`}>
                        {run.status || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50">
          <div className="text-sm font-bold text-gray-900">Legacy delivery reports</div>
          <div className="text-xs text-gray-500 mt-0.5">{reports.length} older export records</div>
        </div>
        {reports.length === 0 ? (
          <EmptyState title="No legacy reports" hint="Older delivery exports will list here if they exist in the database." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">Date</th>
                  <th className="px-6 py-3 text-left font-semibold">Counts</th>
                  <th className="px-6 py-3 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map((r, idx) => (
                  <tr key={r.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                    <td className="px-6 py-4 font-semibold text-gray-900">{r.issue_date || '—'}</td>
                    <td className="px-6 py-4 text-gray-600">del {r.delivery_count} · free {r.free_count} · paid {r.paid_count}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${runStatusBadge(r.status)}`}>
                        {r.status || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
